import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const AboutPage = () => {
  const milestones = [
    { year: "2018", event: "Laguerre founded with a vision for bespoke excellence" },
    { year: "2019", event: "Introduced our signature bespoke suit line" },
    { year: "2020", event: "Expanded to include bridal couture" },
    { year: "2022", event: "Opened our flagship atelier" },
    { year: "2023", event: "Celebrated serving over 1,000 clients" },
    { year: "2024", event: "Launched sustainable fabric collection" }
  ];

  const values = [
    {
      title: "Craftsmanship",
      description: "Every stitch is a testament to our unwavering commitment to excellence. Our master tailors bring decades of experience to each garment."
    },
    {
      title: "Heritage",
      description: "We honor traditional tailoring techniques passed down through generations while embracing innovation that enhances our craft."
    },
    {
      title: "Individuality",
      description: "No two clients are alike, and no two garments should be either. We celebrate your unique style and physique."
    },
    {
      title: "Sustainability",
      description: "Quality that lasts is the ultimate sustainability. We source responsibly and create pieces meant to be treasured for years."
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-28 border-b border-white/5 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/6766284/pexels-photo-6766284.jpeg?w=1920')`
          }}
        />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">Our Story</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6" data-testid="about-page-title">
              About laguerre.co
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto font-light text-lg">
              Crafting custom garments that tell stories, celebrate individuality, 
              and stand the test of time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-[4/5] relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_custom-threads-65/artifacts/71gzxqo9_DSCF0806.jpg"
                  alt="Laguerre clients in custom attire"
                  className="w-full h-full object-cover"
                  data-testid="about-hero-image"
                />
                <div className="absolute inset-0 border border-primary/20 -translate-x-4 -translate-y-4 -z-10" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="mono-text text-primary mb-4">The Beginning</p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-6" data-testid="founder-story-title">
                A Vision of Excellence
              </h2>
              <div className="space-y-4 text-muted-foreground font-light leading-relaxed">
                <p>
                  Laguerre was founded with a singular vision: to bring the refined 
                  techniques of master tailoring to those who appreciate true craftsmanship 
                  and the artistic sensibility of haute couture.
                </p>
                <p>
                  What began as a passion project has grown into a respected bespoke 
                  tailoring house, while never losing sight of the principles that 
                  define us: meticulous attention to detail, uncompromising quality, 
                  and a deep respect for our clients' individual style.
                </p>
                <p>
                  Today, our atelier continues to uphold these traditions, with a team of 
                  master tailors who share our vision of creating garments that are 
                  not merely worn, but experienced.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-card/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="mono-text text-primary mb-4">What We Stand For</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight" data-testid="values-title">
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="p-8 border border-white/5 bg-card/50 hover:border-primary/30 transition-colors duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="font-serif text-xl mb-4 text-primary">{value.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="mono-text text-primary mb-4">Our Journey</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight" data-testid="timeline-title">
              Milestones
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                data-testid={`milestone-${index}`}
                className="flex gap-8 items-start pb-12 last:pb-0"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex flex-col items-center">
                  <span className="mono-text text-primary">{milestone.year}</span>
                  <div className="w-px h-full bg-gradient-to-b from-primary/50 to-transparent mt-4" />
                </div>
                <p className="text-muted-foreground font-light pt-0.5">
                  {milestone.event}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="py-24 bg-card/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="mono-text text-primary mb-4">The Custom Experience</p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-6" data-testid="process-title">
                Our Process
              </h2>
              <div className="space-y-6">
                {[
                  { step: "01", title: "Consultation", desc: "We begin with an in-depth discussion of your style, needs, and vision." },
                  { step: "02", title: "Measurement", desc: "Over 40 precise measurements ensure a perfect fit unique to you." },
                  { step: "03", title: "Fabric Selection", desc: "Choose from our curated collection of the world's finest fabrics." },
                  { step: "04", title: "Creation", desc: "Our master tailors handcraft your garment with meticulous care." },
                  { step: "05", title: "Fitting", desc: "Final adjustments ensure every detail meets our exacting standards." }
                ].map((item, index) => (
                  <div key={item.step} className="flex gap-4" data-testid={`process-step-${index}`}>
                    <span className="mono-text text-primary text-sm">{item.step}</span>
                    <div>
                      <h4 className="font-serif text-lg mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_custom-threads-65/artifacts/rpo0gahb_IMG_9540.jpeg"
                alt="Custom tailoring"
                className="w-full aspect-square object-cover"
                data-testid="process-image"
              />
              <div className="absolute inset-0 border border-primary/20 translate-x-4 translate-y-4 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-6" data-testid="about-cta-title">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-10 font-light">
              Experience the art of custom tailoring. Schedule a private consultation 
              at our atelier.
            </p>
            <Link
              to="/booking"
              data-testid="about-cta-btn"
              className="luxury-btn inline-flex items-center gap-3 bg-primary text-primary-foreground px-10 py-5 text-xs uppercase tracking-[0.2em] hover:bg-foreground transition-all duration-300"
            >
              Book Your Consultation <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
